import { type ReactNode, useRef, useState } from 'react'

import { Command } from 'cmdk'

import { CommandMenuFooter } from '~/components/command-menu/components/CommandMenuFooter'
import { useBackspaceGoBack } from '~/components/command-menu/hooks/useBackspaceGoBack'
import { useCommandMenu } from '~/components/command-menu/hooks/useCommandMenu'
import { useFocusOnMount } from '~/components/command-menu/hooks/useFocusOnMount'
import { COMMAND_PANEL_CLASS } from '~/components/command-menu/styles'
import { CommandMenuViewType } from '~/components/command-menu/types'
import { LoadingSpinner } from '~/components/component-library/LoadingSpinner'

type AsyncCommandMenuItem = {
  id: string
  label: string
  icon?: ReactNode
  onSelect: () => void
}

type AsyncCommandMenuProps = {
  view: CommandMenuViewType
  items: AsyncCommandMenuItem[]
  isLoading: boolean
  placeholder: string
  actionLabel: string
  emptyMessage?: string
  debouncedSearch: string
}

export const AsyncCommandMenu = ({
  view,
  items,
  isLoading,
  placeholder,
  actionLabel,
  emptyMessage = 'No results found',
  debouncedSearch,
}: AsyncCommandMenuProps) => {
  const { searchValue, setSearchValue } = useCommandMenu()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const showLoading = isLoading || searchValue !== debouncedSearch

  useFocusOnMount(inputRef, view)
  useBackspaceGoBack({ enabled: searchValue === '' })

  // Track user's selection, falling back to first item when it's no longer in the list
  const firstId = items[0]?.id ?? ''
  const [selectedValue, setSelectedValue] = useState('')
  const effectiveValue = items.some((i) => i.id === selectedValue)
    ? selectedValue
    : firstId

  return (
    <Command
      loop
      label={placeholder}
      shouldFilter={false}
      value={effectiveValue}
      onValueChange={setSelectedValue}
      className={COMMAND_PANEL_CLASS}
    >
      <div className="relative">
        <Command.Input
          ref={inputRef}
          value={searchValue}
          onValueChange={setSearchValue}
          className="w-full flex-shrink-0 border-b border-gray-100 bg-transparent p-4 py-3 text-base placeholder:text-placeholder focus:outline-none dark:border-gray-800"
          placeholder={placeholder}
        />
        {showLoading && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <LoadingSpinner size="md" thickness="normal" variant="default" />
          </div>
        )}
      </div>
      <Command.List className="w-full scroll-py-1 overflow-y-auto">
        <div className="p-1">
          {!showLoading && items.length === 0 && (
            <div className="px-3 py-2 text-sm text-secondary">
              {emptyMessage}
            </div>
          )}
          {items.map((item) => (
            <Command.Item
              key={item.id}
              value={item.id}
              onSelect={item.onSelect}
              className="flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-gray-100 data-[selected=true]:bg-gray-100 dark:hover:bg-gray-800 dark:data-[selected=true]:bg-gray-800"
            >
              {item.icon && (
                <div className="flex h-4 w-4 flex-shrink-0 items-center justify-center [&>svg]:size-4 [&>svg]:stroke-[1.5]">
                  {item.icon}
                </div>
              )}
              <span className="min-w-0 truncate">{item.label}</span>
            </Command.Item>
          ))}
        </div>
      </Command.List>
      <CommandMenuFooter
        actions={[
          { shortcuts: ['enter'], label: actionLabel },
          { shortcuts: ['up', 'down'], label: 'Navigate' },
        ]}
      />
    </Command>
  )
}
